// The pure half of the file picker (ui/FilePicker.tsx): what it says beside its
// button, and whether a chosen file is one it asked for.

/** What the picker says beside its button: the chosen file's name, or that none is chosen. */
export function chosenLabel(name: string | undefined, noneChosen: string): string {
  return name ? name : noneChosen
}

/**
 * What the picker says beside its button after a choice: that the file just picked is
 * not one it takes, when it is not, and otherwise the chosen file's name or that none is
 * chosen. A refused file is named, since the dialog has closed and the person may not
 * remember which one they clicked.
 */
export function pickerNote(
  chosen: string | undefined,
  refused: string | undefined,
  words: { noneChosen: string; notAccepted: (name: string) => string },
): { text: string; refused: boolean } {
  if (refused) return { text: words.notAccepted(refused), refused: true }
  return { text: chosenLabel(chosen, words.noneChosen), refused: false }
}

/**
 * Whether a file's name or type matches an accept list ("image/png,.csv,video/*"), as the
 * browser's own filter would. Checked again here because a picker's filter is a suggestion
 * that "All files" in the dialog ignores.
 *
 * An entry that starts with a dot is an extension, matched against the end of the name
 * whatever its case; one that ends in "/*" is a family of types; anything else is an exact
 * type. An empty list asks for nothing in particular, so it accepts anything.
 */
export function accepts(accept: string | undefined, file: { name: string; type: string }): boolean {
  const entries = (accept ?? '')
    .split(',')
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry) => entry !== '')
  if (entries.length === 0) return true
  const name = file.name.toLowerCase()
  const type = file.type.toLowerCase()
  return entries.some((entry) => {
    if (entry.startsWith('.')) return name.endsWith(entry)
    if (entry.endsWith('/*')) return type !== '' && type.startsWith(entry.slice(0, -1))
    return type === entry
  })
}
